import { all, put, takeEvery, select } from "redux-saga/effects";
import nanoid from "nanoid";
import { isEmpty } from "lodash";

import { cloneDeep } from "../../utils/cloneDeep";
import { ADDRESS_TYPES } from "../../constants";
import { getSignatoryId } from "../../utils/helper";
import {
  CREATE_NEW_STAKEHOLDER,
  DELETE_STAKEHOLDER,
  changeEditableStakeholder,
  updateStakeholdersIds,
  RESET_STAKEHOLDER_INFO
} from "../actions/stakeholders";
import { removeSignatory } from "../actions/completedSteps";
import { updateProspect, updateRoProspect } from "../actions/appConfig";
import {
  getSignatories,
  getROSignatories,
  getSignatoryModel,
  getRoSignatoryModel,
  getFreeFieldsInfo
} from "../selectors/appConfig";
import { getStakeholdersIds } from "../selectors/stakeholders";
import { getIsEditableStatusSearchInfo } from "../selectors/searchProspect";
import { UAE } from "../../constants";
import { saveAndRetrieveDocDetails } from "../actions/uploadDocuments";

export function* createNewStakeholderSaga() {
  const stakeholders = yield select(getSignatories);
  let roStakeholders = cloneDeep(yield select(getROSignatories));
  if (isEmpty(stakeholders)) {
    roStakeholders = [];
  }
  const stakeholdersIds = yield select(getStakeholdersIds);
  const { freeField4 = "" } = yield select(getFreeFieldsInfo);
  const newStakeholder = cloneDeep(yield select(getSignatoryModel));
  const newRoStakeholder = cloneDeep(yield select(getRoSignatoryModel));
  const { signatoryId, signatoryIds } = getSignatoryId(freeField4);
  const isComeFromROScreens = yield select(getIsEditableStatusSearchInfo);

  let addressArray = [];
  ADDRESS_TYPES.forEach((address, index) => {
    let addressStruct = cloneDeep(newStakeholder.addressInfo[0]);
    if (index === 2) {
      addressStruct.addressDetails.country = UAE;
    }
    addressStruct.typeOfAddress = address;
    addressArray.push(addressStruct);
  });

  newStakeholder.addressInfo = addressArray;
  newStakeholder.signatoryId = signatoryId;
  newStakeholder.shareHoldingPercentage = "0";
  newStakeholder.nonCifEntityId = "";

  const stakeholderId = nanoid();
  const updatedStakeholdersIds = [...stakeholdersIds, stakeholderId];

  yield put(updateStakeholdersIds(updatedStakeholdersIds));
  yield put(changeEditableStakeholder(stakeholderId));
  yield put(
    updateProspect({
      "prospect.signatoryInfo": [...stakeholders, newStakeholder],
      "roProspect.signatoryInfo": [...roStakeholders, newRoStakeholder],
      "prospect.freeFieldsInfo.freeField4": JSON.stringify(signatoryIds)
    })
  );
  yield put(
    updateRoProspect({
      "roProspect.signatoryInfo": [...roStakeholders, newRoStakeholder]
    })
  );

  if (isComeFromROScreens) {
    yield put(saveAndRetrieveDocDetails());
  }
}

export function* deleteStakeholderSaga({ payload: stakeholderId }) {
  const stakeholdersIds = yield select(getStakeholdersIds);
  const stakeholders = yield select(getSignatories);
  const roStakeholders = yield select(getROSignatories);

  const indexToRemove = stakeholdersIds.indexOf(stakeholderId);

  yield put(
    updateProspect({ "prospect.signatoryInfo": stakeholders.filter((_, i) => i !== indexToRemove) })
  );
  yield put(
    updateRoProspect({
      "roProspect.signatoryInfo": roStakeholders.filter((_, i) => i !== indexToRemove)
    })
  );
  yield put(updateStakeholdersIds(stakeholdersIds.filter(id => id !== stakeholderId)));
  yield put(removeSignatory(stakeholderId));
  yield put(changeEditableStakeholder(null));
}
export function* resetStakeholderSaga({ payload: staekholderIndex }) {
  const stakeholders = cloneDeep(yield select(getSignatories));
  const newStakeholder = yield select(getSignatoryModel);

  const resetedStakeholder = {
    ...newStakeholder,
    signatoryId: stakeholders[staekholderIndex].signatoryId,
    stakeholdersDocuments: stakeholders[staekholderIndex].stakeholdersDocuments,
    addressInfo: stakeholders[staekholderIndex].addressInfo,
    shareHoldingPercentage: "0",
    nonCifEntityId: stakeholders[staekholderIndex].nonCifEntityId ?? ""
  };
  yield put(
    updateProspect({ [`prospect.signatoryInfo[${staekholderIndex}]`]: resetedStakeholder })
  );
}

export default function* stakeholderSaga() {
  yield all([
    takeEvery(CREATE_NEW_STAKEHOLDER, createNewStakeholderSaga),
    takeEvery(DELETE_STAKEHOLDER, deleteStakeholderSaga),
    takeEvery(RESET_STAKEHOLDER_INFO, resetStakeholderSaga)
  ]);
}
